/**
 * Ce qu'une série fait travailler — **la même pondération des deux côtés**.
 *
 * 🔴 **Cinquième fichier de `shared/`, même motif que `search.ts` et
 * `macroTarget.ts`.** Deux écrans du projet comptent des séries par muscle, à
 * partir de deux sources différentes :
 *
 *   - la **carte corporelle** d'une routine (`web/src/screens/musculation/
 *     muscleVolume.ts`, 29/08/2026) — ce qu'une routine *prévoit* ;
 *   - le **volume par groupe musculaire et par semaine** du tableau de bord
 *     (`server/src/services/stats.ts`, étape 9) — ce qu'on a *fait*.
 *
 * Les entrées diffèrent (des lignes de routine d'un côté, des `actual_set` de
 * l'autre), la **règle de pondération** non. Si les deux divergeaient, une
 * routine annoncée « 12 séries de pectoraux » produirait un tableau de bord
 * disant 8 pour la même séance exécutée sans écart, et rien n'expliquerait la
 * différence. C'est le symptôme de `shared/search.ts` transposé : personne ne
 * le signale, on se dit qu'on a mal compté.
 *
 * ⚠️ **Ce fichier ne porte QUE la pondération**, jamais la façon d'obtenir les
 * séries. Chacun des deux appelants garde la sienne — l'un déroule les tours
 * d'un circuit, l'autre lit des séries déjà faites — et c'est bien pour ça que
 * le partage s'arrête ici : mettre en commun ce qui n'est pas commun aurait
 * demandé un dénominateur artificiel.
 */

/**
 * Poids d'un muscle **secondaire** dans le total : ni un, ni zéro.
 *
 * 🔴 Le raisonnement, posé le 29/08/2026 avec la carte corporelle et repris
 * tel quel : « une série de développé couché ne travaille pas les triceps
 * autant que les pectoraux, mais elle les travaille. Les compter pleins ferait
 * dire que tout est sollicité pareil ; les ignorer laisserait des bras blancs
 * sur une séance qui les a bien employés. »
 *
 * ⚠️ **C'est un choix d'affichage, et il est assumé comme tel.** Un demi n'est
 * pas une mesure physiologique : c'est la façon la moins fausse de dire « ça
 * compte, mais moins ». Le tableau de bord le **dit** sous le graphique, comme
 * la carte le dit sous elle.
 */
export const SECONDARY_MUSCLE_WEIGHT = 0.5;

/**
 * Répartit `sets` séries sur les muscles d'un exercice, en accumulant dans
 * `into`.
 *
 * ⚠️ **Accumule plutôt qu'elle ne rend une carte**, parce que les deux
 * appelants bouclent : allouer un `Map` par ligne de routine ou par série
 * ferait 26 000 allocations sur le jeu de cinq ans, pour un résultat identique.
 *
 * ⚠️ **Ne filtre PAS l'échauffement** : les deux appelants l'écartent
 * eux-mêmes, chacun sur son propre champ (`setKind` d'une ligne de routine ici,
 * `actual_set.set_kind` là). Le faire ici demanderait de passer une série
 * entière à une fonction qui n'a besoin que de ses muscles — et surtout
 * laisserait croire qu'elle connaît les règles d'inclusion, alors qu'elle n'en
 * connaît qu'une : la pondération.
 */
export function weighMuscleSets(
  primaryMuscles: readonly string[],
  secondaryMuscles: readonly string[],
  sets: number,
  into: Map<string, number>,
): void {
  for (const muscle of primaryMuscles) {
    into.set(muscle, (into.get(muscle) ?? 0) + sets);
  }
  for (const muscle of secondaryMuscles) {
    into.set(muscle, (into.get(muscle) ?? 0) + sets * SECONDARY_MUSCLE_WEIGHT);
  }
}
